import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { ESTOQUE_PAGE } from "../routes";
import {
  getMassBalanceStoresApi,
  getMassBalanceReportApi,
  getMassBalanceReportExcelApi,
  getEntradasSaidasReportApi,
  getEntradasSaidasReportExcelApi,
  getDeficitNotificationsApi,
  getDeficitReportExcelApi,
  StoreShort,
  DeficitNotification
} from "../api/massBalanceApi";

import { useGoBack } from "../../../hooks/useGoBack";

type ActiveTab = "report" | "inout" | "deficit";

export default function ReportsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(ESTOQUE_PAGE());
  const location = useLocation();
  const token = useSelector((state: RootState) => state.auth.accessToken);

  // Read initial tab from query string (?tab=deficit etc)
  const initialTab = useMemo<ActiveTab>(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam === "deficit" || tabParam === "inout" || tabParam === "report") {
      return tabParam as ActiveTab;
    }
    return "report";
  }, [location.search]);

  const [activeTab, setActiveTab] = useState<ActiveTab>("report");

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const [stores, setStores] = useState<StoreShort[]>([]);

  // Filter values
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // OPEN, CLOSED, RECOUNTED
  const [ufFilter, setUfFilter] = useState(""); // UF for inout
  const [showAllDeficits, setShowAllDeficits] = useState(false);


  // Report results
  const [balances, setBalances] = useState<any[]>([]);
  const [inouts, setInouts] = useState<any[]>([]);
  const [deficits, setDeficits] = useState<DeficitNotification[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const loadStores = async () => {
      if (!token) return;
      try {
        const data = await getMassBalanceStoresApi(token);
        setStores(data || []);
      } catch (err: any) {
        toast.error(err.message || "Erro ao carregar lojas.");
      }
    };
    loadStores();
  }, [token]);

  // Load selected report based on filters & active tab
  const loadReports = async () => {
    if (!token) return;
    try {
      setLoading(true);
      if (activeTab === "report") {
        const params: any = {
          storeIds: selectedStoreId ? [selectedStoreId] : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          search: search.trim() || undefined,
          status: statusFilter ? [statusFilter] : undefined,
        };
        const data = await getMassBalanceReportApi(token, params);
        setBalances(data || []);
      } else if (activeTab === "inout") {
        const params: any = {
          storeIds: selectedStoreId ? [selectedStoreId] : undefined,
          estados: ufFilter ? [ufFilter] : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          search: search.trim() || undefined,
        };
        const data = await getEntradasSaidasReportApi(token, params);
        setInouts(data || []);
      } else if (activeTab === "deficit") {
        const data = await getDeficitNotificationsApi(token, showAllDeficits);
        setDeficits(data || []);
      }
    } catch (err: any) {
      toast.error(err.message || "Erro ao buscar relatório.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [token, activeTab, selectedStoreId, startDate, endDate, search, statusFilter, ufFilter, showAllDeficits]);

  // Excel Exports
  const handleExportBalanceExcel = async () => {
    if (!token) return;
    try {
      toast.info("Processando download do Excel...");
      const params: any = {
        storeIds: selectedStoreId ? [selectedStoreId] : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        search: search.trim() || undefined,
        status: statusFilter ? [statusFilter] : undefined,
      };
      const blob = await getMassBalanceReportExcelApi(token, params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_balanco_massa_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      toast.success("Download completo!");
    } catch (err: any) {
      toast.error(err.message || "Erro ao exportar Excel.");
    }
  };

  const handleExportInoutExcel = async () => {
    if (!token) return;
    try {
      toast.info("Processando download do Excel...");
      const params: any = {
        storeIds: selectedStoreId ? [selectedStoreId] : undefined,
        estados: ufFilter ? [ufFilter] : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        search: search.trim() || undefined,
      };
      const blob = await getEntradasSaidasReportExcelApi(token, params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_entradas_saidas_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      toast.success("Download completo!");
    } catch (err: any) {
      toast.error(err.message || "Erro ao exportar Excel.");
    }
  };

  const handleDownloadDeficit = async (id: string) => {
    if (!token) return;
    try {
      toast.info("Gerando relatório...");
      const blob = await getDeficitReportExcelApi(token, id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_deficit_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      toast.success("Relatório baixado!");
      loadReports();
    } catch (err: any) {
      toast.error(err.message || "Erro ao baixar relatório.");
    }
  };

  const [reportPage, setReportPage] = useState(1);
  const [reportLimit, setReportLimit] = useState(10);

  const flattenedReportRows = useMemo(() => {
    if (activeTab !== "report") return [];
    const rows: any[] = [];
    for (const b of balances) {
      for (const item of (b.items || [])) {
        if (!item.isTracked) continue;
        const valor = Number(item.valor) || 0;
        const varCont = item.variacaoContagem !== null && item.variacaoContagem !== undefined ? Number(item.variacaoContagem) : null;
        const ttVal = varCont !== null ? varCont * valor : 0;
        const varRec = item.variacaoRecontagem !== null && item.variacaoRecontagem !== undefined ? Number(item.variacaoRecontagem) : null;
        const ttRecVal = varRec !== null ? varRec * valor : 0;

        rows.push({
          loja: b.store?.nomeFantasia || "—",
          uf: b.store?.estado || "—",
          data: new Date(b.referenceDate).toLocaleDateString("pt-BR", { timeZone: "UTC" }),
          status: b.status,
          responsavel: b.createdBy?.nomeCompleto || "—",
          ean: item.sku,
          descricao: item.descricao,
          valor: valor,
          contAnterior: item.contagemAnterior,
          dataContAnt: item.dataContagemAnterior ? new Date(item.dataContagemAnterior).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "—",
          entrega: item.entrega,
          vendas: item.venda,
          devolucao: item.devolucao,
          avarias: item.avarias,
          vencimentos: item.vencimentos,
          consumos: item.consumos,
          projetado: item.projetado,
          contagem: item.contagem !== null && item.contagem !== undefined ? item.contagem : null,
          variacao: varCont,
          tt: ttVal,
          recontagem: item.recontagem !== null && item.recontagem !== undefined ? item.recontagem : null,
          varRecontagem: varRec,
          ttRecontagem: ttRecVal,
          ajusteEstoque: item.ajusteEstoque !== null && item.ajusteEstoque !== undefined ? item.ajusteEstoque : null,
          ajusteJustificativaTexto: item.ajusteJustificativaTexto || ""
        });
      }
    }
    return rows;
  }, [balances, activeTab]);

  const paginatedReportRows = useMemo(() => {
    return flattenedReportRows.slice((reportPage - 1) * reportLimit, reportPage * reportLimit);
  }, [flattenedReportRows, reportPage, reportLimit]);

  const reportTotalPages = useMemo(() => {
    return Math.ceil(flattenedReportRows.length / reportLimit) || 1;
  }, [flattenedReportRows, reportLimit]);

  // Reset page when search/filters/tab change
  useEffect(() => {
    setReportPage(1);
  }, [balances, reportLimit]);

  if (!token) return null;

  return (
    <div className="estoqueLayout">
      <main className="estoqueMain">
        <div className="estoqueContainer">
          <EcoPageHeader
            crumbs={[{ label: "Estoque", onClick: () => navigate(ESTOQUE_PAGE()) }, { label: "Relatórios" }]}
            title="Relatórios de estoque"
            subtitle="Balanços diários finalizados, movimentações de produtos e ocorrências, para consultar e exportar."
            onBack={goBack}
            backLabel="Voltar para Estoque"
          />

          <EcoSegmented
            label="Tipo de relatório"
            className="estoqueTabs"
            options={[
              { value: "report", label: "Relatório de balanço" },
              { value: "inout", label: "Entradas e saídas" },
              { value: "deficit", label: "Notificações de déficit" },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />

          {/* Filters Area */}
          {activeTab !== "deficit" && (
            <section className="estoqueControls" style={{ background: "var(--eco-ground)", padding: 16, borderRadius: 8, marginBottom: 20 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
                <div className="estoqueFormGroup">
                  <label>Loja</label>
                  <select
                    className="estoqueSelect"
                    value={selectedStoreId}
                    onChange={(e) => setSelectedStoreId(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nomeFantasia}
                      </option>
                    ))}
                  </select>
                </div>

                {activeTab === "report" && (
                  <div className="estoqueFormGroup">
                    <label>Status</label>
                    <select
                      className="estoqueSelect"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">Todos</option>
                      <option value="OPEN">OPEN</option>
                      <option value="RECOUNTED">RECOUNTED</option>
                      <option value="CLOSED">CLOSED</option>
                    </select>
                  </div>
                )}

                {activeTab === "inout" && (
                  <div className="estoqueFormGroup">
                    <label>Estado (UF)</label>
                    <input
                      type="text"
                      className="estoqueInput"
                      style={{ minWidth: 80, width: 80 }}
                      maxLength={2}
                      placeholder="SP"
                      value={ufFilter}
                      onChange={(e) => setUfFilter(e.target.value.toUpperCase())}
                    />
                  </div>
                )}

                <div className="estoqueFormGroup">
                  <label>Data Início</label>
                  <input
                    type="date"
                    className="estoqueInput"
                    style={{ minWidth: 140 }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="estoqueFormGroup">
                  <label>Data Fim</label>
                  <input
                    type="date"
                    className="estoqueInput"
                    style={{ minWidth: 140 }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className="estoqueFormGroup">
                  <label>Pesquisa textual</label>
                  <input
                    type="text"
                    className="estoqueInput"
                    placeholder="Descrição..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <button
                  className="estoqueBtn estoqueBtn--primary"
                  onClick={activeTab === "report" ? handleExportBalanceExcel : handleExportInoutExcel}
                >
                  Exportar Excel
                </button>
              </div>
            </section>
          )}


          {activeTab === "deficit" && (
            <section className="estoqueControls" style={{ background: "var(--eco-ground)", padding: 16, borderRadius: 8, marginBottom: 20 }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={showAllDeficits}
                  onChange={(e) => setShowAllDeficits(e.target.checked)}
                />
                Exibir histórico completo (incluindo baixadas/arquivadas)
              </label>
            </section>
          )}

          {/* Results Lists */}
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}>Carregando dados...</div>
          ) : (
            <>
              {activeTab === "report" && (
                <>
                  <div style={{ marginBottom: 12, fontSize: 13, color: "var(--eco-muted)" }}>
                    <strong>{balances.length}</strong> balanço(s) encontrado(s) — <strong>{flattenedReportRows.length}</strong> item(ns) no total
                  </div>

                  <div className="estoqueTableWrapper" style={{ overflowX: "auto" }}>
                    <table className="estoqueTable" style={{ minWidth: 2000 }}>
                      <thead>
                        <tr>
                          <th>Loja</th>
                          <th>UF</th>
                          <th>Data</th>
                          <th>Status</th>
                          <th>Responsável</th>
                          <th>Código EAN</th>
                          <th>Descrição</th>
                          <th>Valor (R$)</th>
                          <th>Cont. Anterior</th>
                          <th>Data Cont. Ant.</th>
                          <th>Entrega</th>
                          <th>Vendas</th>
                          <th>Devolução</th>
                          <th>Avarias</th>
                          <th>Vencimentos</th>
                          <th>Consumos</th>
                          <th>Projetado</th>
                          <th>Contagem</th>
                          <th>Variação</th>
                          <th>TT (R$)</th>
                          <th>Recontagem</th>
                          <th>Var. Recontagem</th>
                          <th>TT Recontagem (R$)</th>
                          <th>Ajuste de Estoque</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedReportRows.length === 0 ? (
                          <tr>
                            <td colSpan={24} style={{ textAlign: "center", padding: 20, color: "var(--eco-muted)" }}>
                              Nenhum item de balanço encontrado para os filtros selecionados.
                            </td>
                          </tr>
                        ) : (
                          paginatedReportRows.map((row, idx) => (
                            <tr key={idx}>
                              <td>{row.loja}</td>
                              <td>{row.uf}</td>
                              <td>{row.data}</td>
                              <td>
                                {row.status === "OPEN" && (
                                  <span className="estoqueBadge" style={{ background: "var(--eco-status-warn-soft)", color: "var(--eco-status-warn)" }}>Aberto</span>
                                )}
                                {row.status === "RECOUNTED" && (
                                  <span className="estoqueBadge" style={{ background: "#e8eef9", color: "#2b4c8c" }}>Recontado</span>
                                )}
                                {row.status === "CLOSED" && (
                                  <span className="estoqueBadge" style={{ background: "var(--eco-status-good-soft)", color: "var(--eco-status-good)" }}>Encerrado</span>
                                )}
                              </td>
                              <td>{row.responsavel}</td>
                              <td>{row.ean}</td>
                              <td>{row.descricao}</td>
                              <td>R$ {row.valor.toFixed(2)}</td>
                              <td>{row.contAnterior}</td>
                              <td>{row.dataContAnt}</td>
                              <td>{row.entrega}</td>
                              <td>{row.vendas}</td>
                              <td>{row.devolucao}</td>
                              <td>{row.avarias}</td>
                              <td>{row.vencimentos}</td>
                              <td>{row.consumos}</td>
                              <td>{row.projetado}</td>
                              <td>{row.contagem !== null ? row.contagem : "—"}</td>
                              <td>{row.variacao !== null ? row.variacao : "—"}</td>
                              <td>
                                {row.variacao !== null ? (
                                  row.tt === 0 ? (
                                    "R$ 0.00"
                                  ) : row.tt < 0 ? (
                                    <span style={{ color: "var(--eco-danger)", fontWeight: 600 }}>- R$ {Math.abs(row.tt).toFixed(2)}</span>
                                  ) : (
                                    <span style={{ fontWeight: 600 }}>R$ {row.tt.toFixed(2)}</span>
                                  )
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>{row.recontagem !== null ? row.recontagem : "—"}</td>
                              <td>{row.varRecontagem !== null ? row.varRecontagem : "—"}</td>
                              <td>
                                {row.varRecontagem !== null ? (
                                  row.ttRecontagem === 0 ? (
                                    "R$ 0.00"
                                  ) : row.ttRecontagem < 0 ? (
                                    <span style={{ color: "var(--eco-danger)", fontWeight: 600 }}>- R$ {Math.abs(row.ttRecontagem).toFixed(2)}</span>
                                  ) : (
                                    <span style={{ fontWeight: 600 }}>R$ {row.ttRecontagem.toFixed(2)}</span>
                                  )
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>
                                {row.ajusteEstoque !== null ? (
                                  `${row.ajusteEstoque} ${row.ajusteJustificativaTexto ? ` - ${row.ajusteJustificativaTexto}` : ""}`
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {flattenedReportRows.length > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--eco-muted)" }}>
                        Itens por página:
                        <select
                          className="estoqueSelect"
                          style={{ width: 75, height: 32, minWidth: "auto", padding: 4 }}
                          value={reportLimit}
                          onChange={(e) => {
                            setReportLimit(Number(e.target.value));
                            setReportPage(1);
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <button
                          className="estoqueBtn estoqueBtn--secondary"
                          style={{ height: 32, padding: "0 12px", fontSize: 13 }}
                          disabled={reportPage === 1}
                          onClick={() => setReportPage(p => Math.max(1, p - 1))}
                        >
                          Anterior
                        </button>
                        <span style={{ fontSize: 13, color: "var(--eco-ink-2)" }}>
                          Página <strong>{reportPage}</strong> de <strong>{reportTotalPages}</strong>
                        </span>
                        <button
                          className="estoqueBtn estoqueBtn--secondary"
                          style={{ height: 32, padding: "0 12px", fontSize: 13 }}
                          disabled={reportPage === reportTotalPages}
                          onClick={() => setReportPage(p => Math.min(reportTotalPages, p + 1))}
                        >
                          Próxima
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === "inout" && (
                <div className="estoqueTableWrapper">
                  <table className="estoqueTable">
                    <thead>
                      <tr>
                        <th>SKU (EAN)</th>
                        <th>Cod Admin</th>
                        <th>Descrição</th>
                        <th>Saldo Inicial</th>
                        <th>Entradas</th>
                        <th>Vendas</th>
                        <th>Avarias</th>
                        <th>Vencimentos</th>
                        <th>Consumo</th>
                        <th>Devoluções</th>
                        <th>Projetado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inouts.length === 0 ? (
                        <tr>
                          <td colSpan={11} style={{ textAlign: "center", padding: 20, color: "var(--eco-muted)" }}>
                            Nenhuma movimentação de produto encontrada.
                          </td>
                        </tr>
                      ) : (
                        inouts.map((i, idx) => (
                          <tr key={idx}>
                            <td>{i.sku}</td>
                            <td>{i.numeroItem || "-"}</td>
                            <td>{i.descricao}</td>
                            <td>{i.contagemAnterior}</td>
                            <td>{i.entrega}</td>
                            <td>{i.venda}</td>
                            <td>{i.avarias}</td>
                            <td>{i.vencimentos}</td>
                            <td>{i.consumos}</td>
                            <td>{i.devolucao}</td>
                            <td>
                              <strong>{i.projetado}</strong>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "deficit" && (
                <div className="estoqueTableWrapper">
                  <table className="estoqueTable">
                    <thead>
                      <tr>
                        <th>Loja</th>
                        <th>Data Balanço</th>
                        <th>Data Notificação</th>
                        <th>Produtos com Ajuste</th>
                        <th>Status</th>
                        <th style={{ width: 180, textAlign: "center" }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deficits.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: "center", padding: 20, color: "var(--eco-muted)" }}>
                            Nenhuma notificação de déficit de estoque encontrada.
                          </td>
                        </tr>
                      ) : (
                        deficits.map((d) => (
                          <tr key={d.id}>
                            <td>{d.store?.nomeFantasia}</td>
                            <td>
                              {d.massBalance?.referenceDate
                                ? new Date(d.massBalance.referenceDate).toLocaleDateString("pt-BR", { timeZone: "UTC" })
                                : "-"}
                            </td>
                            <td>{new Date(d.createdAt).toLocaleString()}</td>
                            <td>
                              <span className="estoqueBadge estoqueBadge--orange">{d.itemCount} item(ns)</span>
                            </td>
                            <td>
                              {d.dismissedAt ? (
                                <span className="estoqueBadge estoqueBadge--blue">Arquivada</span>
                              ) : (
                                <span className="estoqueBadge estoqueBadge--red">Pendente</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: "flex", justifyContent: "center" }}>
                                <button
                                  className="estoqueBtn estoqueBtn--primary"
                                  style={{ height: 32, padding: "0 12px" }}
                                  onClick={() => handleDownloadDeficit(d.massBalanceId)}
                                >
                                  Baixar Excel
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}


            </>
          )}
        </div>
      </main>
    </div>
  );
}
